/**
 * All businesses mode — what a group owner (tenant owner / super admin with
 * more than one business) can open without choosing a business first.
 *
 *   scope "company"  the page is company-wide already (team, chat, audit
 *                    log, subscription) — no business bar
 *   scope "one"      the page works on one business at a time; it shows its
 *                    own business bar (default: the business last viewed,
 *                    else the primary one). Nothing global changes.
 *
 * Selling and stock (till, orders, products …) still happen inside a
 * business: those pages ask to open one.
 */
export type GroupScope = "company" | "one";

export const GROUP_MODE_PAGES: { prefix: string; scope: GroupScope }[] = [
  { prefix: "/group", scope: "company" },
  { prefix: "/portfolio", scope: "company" },
  { prefix: "/select-business", scope: "company" },
  { prefix: "/settings/profile", scope: "company" },
  { prefix: "/team-chat", scope: "company" },
  { prefix: "/team", scope: "company" },
  { prefix: "/audit-log", scope: "company" },
  { prefix: "/action-history", scope: "company" },
  { prefix: "/subscriptions", scope: "company" },
  // One business at a time, chosen on the page.
  { prefix: "/settings/locations", scope: "one" },
  { prefix: "/settings", scope: "one" },
  { prefix: "/expenditures", scope: "one" },
  { prefix: "/admin/shifts", scope: "one" },
  { prefix: "/reports", scope: "one" },
  { prefix: "/hr", scope: "one" },
  { prefix: "/payroll", scope: "one" },
  { prefix: "/my", scope: "one" },
];

export function groupScopeFor(pathname: string): GroupScope | null {
  const hit = GROUP_MODE_PAGES.find((p) => pathname === p.prefix || pathname.startsWith(`${p.prefix}/`) || (p.prefix.endsWith("/") && pathname.startsWith(p.prefix)));
  return hit ? hit.scope : null;
}

/**
 * Money pages that can also show every business together (converted to one
 * currency). Exact paths — a single payroll run still opens in its business.
 */
export type TogetherView = "expenses" | "payroll";
const TOGETHER: Record<string, TogetherView> = { "/expenditures": "expenses", "/payroll": "payroll" };
export function togetherViewFor(pathname: string): TogetherView | null {
  return TOGETHER[pathname.replace(/\/+$/, "")] ?? null;
}
